/**
 * Where the signup reference token lives between the form and the thank-you
 * page (spec sections 05, 10, 19).
 *
 * The rule from the spec is short: "After successful signup, route to the
 * audience specific thank you page with a short lived signup reference. Never
 * place personal data in a URL." So the token goes in `sessionStorage` and the
 * URL stays `/thank-you/owner` — nothing to leak into a referrer header, a
 * server log, or a screenshot someone posts in a Facebook group.
 *
 * One key per audience, because a person may legitimately be both an owner and
 * a provider (CLAUDE.md) and signing up as the second must not overwrite the
 * receipt for the first.
 *
 * The token is opaque here. The client only ever asks "is there one?" — it is
 * signed and verified by the server (services/signupRef.ts), and the Stage B
 * referral endpoint is what actually checks it.
 */

export type SignupAudience = 'owner' | 'provider';

const STORAGE_KEY_PREFIX = 'mhb.signupRef.';

function storageKey(audience: SignupAudience): string {
  return `${STORAGE_KEY_PREFIX}${audience}`;
}

/** Stores the receipt for a signup that just succeeded. */
export function storeSignupRef(audience: SignupAudience, signupRef: string): void {
  try {
    window.sessionStorage.setItem(storageKey(audience), signupRef);
  } catch {
    // Storage is unavailable. The signup itself already succeeded; the
    // thank-you page will send them back to the form, which is a poor
    // experience but not a lost lead.
  }
}

/** The stored receipt, or null. Presence is all the client checks. */
export function readSignupRef(audience: SignupAudience): string | null {
  try {
    const value = window.sessionStorage.getItem(storageKey(audience));
    return value === null || value.trim() === '' ? null : value;
  } catch {
    return null;
  }
}

export function clearSignupRef(audience: SignupAudience): void {
  try {
    window.sessionStorage.removeItem(storageKey(audience));
  } catch {
    // nothing to clear
  }
}
